import { useEffect, useRef, useState } from 'react'

import { Working } from '~/components/working'
import { useBeat, useWork } from '~/components/use-work'
import { NoteIcon, UploadIcon } from '~/components/icons'
import { useApi } from '~/data/data-context'
import type { Food } from '~/data/nutrition'
import { codeReader } from '~/lib/barcode-reader'
import { cameraMode, keepCameraMode, type CameraMode } from '~/lib/camera-mode'
import { downscaleToJpeg } from '~/lib/downscale-image'

/** How often a frame is read for a code. */
const READ_EVERY_MS = 250

/** How long a code that could not be looked up is shown for before reading again. */
const RETRY_AFTER_MS = 1500

const PRIMARY =
  'rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50'

const QUIET = 'rounded-full px-4 py-2 text-sm text-accent transition-colors hover:bg-hover'

/** A round control laid over the camera's picture, lit enough to be read against anything. */
const OVER =
  'grid size-12 place-items-center rounded-full bg-black/45 text-white ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-black/65'

/**
 * The camera — the nutrition spec, Logging an entry. One way whether it is looking at the food
 * or at the packet: a control on it says which of the two it is for, and pressing that changes
 * what it is looking for without leaving the camera. Looking at the food, a photo is taken, or
 * picked from the device, or asking goes on with words alone; looking at the packet, a code is
 * read off the frame and the food carrying it is opened, and a code no food carries offers
 * making one with the code already on it.
 *
 * The picture is the whole of the page, and everything else stands over it: the two-way
 * control along its top, the shutter and the ways round it along its foot, each on a fade so
 * the words are read against whatever the lens is looking at.
 *
 * Where the camera cannot be opened — no camera, or no leave to use it — the photo is picked
 * and the code is typed, so nothing the camera does is only done through it.
 */
export function CameraWay({
  onPhoto,
  onWords,
  onFound,
  onCreate,
  onGiveUp,
}: {
  /** A photo of the food, downscaled and as it will be sent. */
  onPhoto: (image: string) => void
  /** Asking with words alone. */
  onWords: () => void
  /** The food a scanned code turned out to carry. */
  onFound: (food: Food) => void
  /** Making a food for a code none carries. */
  onCreate: (code: string) => void
  /** Giving up on a code none carries. */
  onGiveUp: () => void
}) {
  const api = useApi()
  const [mode, setMode] = useState<CameraMode>(cameraMode)
  const video = useRef<HTMLVideoElement>(null)
  /** Whether the camera opened, is still opening, or could not be opened. */
  const [lens, setLens] = useState<'opening' | 'open' | 'closed'>('opening')
  const [reading, setReading] = useState(false)
  /** A code being looked up, during which the frame is not read again. */
  const [lookingUp, setLookingUp] = useState<string | null>(null)
  const lookingUpShown = useBeat(lookingUp !== null)
  /** A code no food carries, standing in for the camera until it is answered. */
  const [missing, setMissing] = useState<string | null>(null)
  /** What the camera says under itself, where it has something to say. */
  const [said, setSaid] = useState<string | null>(null)
  const [typed, setTyped] = useState('')

  const look = (next: CameraMode) => {
    keepCameraMode(next)
    setMode(next)
  }

  // The one back camera, opened once and let go of as the way is left.
  useEffect(() => {
    const element = video.current
    const devices = navigator.mediaDevices as MediaDevices | undefined
    if (element === null || devices?.getUserMedia === undefined) {
      setLens('closed')
      return
    }
    let stream: MediaStream | null = null
    let alive = true
    devices.getUserMedia({ video: { facingMode: 'environment' } }).then(
      (got) => {
        if (!alive) {
          got.getTracks().forEach((track) => {
            track.stop()
          })
          return
        }
        stream = got
        element.srcObject = got
        void element.play().catch(() => undefined)
        setLens('open')
      },
      () => {
        if (alive) {
          setLens('closed')
        }
      },
    )
    return () => {
      alive = false
      stream?.getTracks().forEach((track) => {
        track.stop()
      })
    }
  }, [])

  const lookUp = async (code: string) => {
    setLookingUp(code)
    setSaid(null)
    try {
      const food = await api.foodByBarcode(code)
      if (food === null) {
        setMissing(code)
      } else {
        onFound(food)
      }
    } catch {
      /*
       * Could not be looked up, which is not "no food carries it": that answer is the server's
       * to give, and only over a request that reached it. The camera reads the code again
       * after a moment, so a hiccup costs a second where a verdict would have cost a duplicate.
       */
      setSaid('The code could not be looked up. Trying again…')
      await new Promise((resolve) => setTimeout(resolve, RETRY_AFTER_MS))
    } finally {
      setLookingUp(null)
    }
  }

  // Looking at the packet, the frame is read for a code every so often until one is found.
  const scanning = mode === 'barcode' && lens === 'open' && lookingUp === null && missing === null
  useEffect(() => {
    if (!scanning) {
      return
    }
    const element = video.current
    if (element === null) {
      return
    }
    let alive = true
    let timer = 0
    setReading(true)
    void codeReader().then((reader) => {
      if (!alive) {
        return
      }
      const read = async () => {
        try {
          const codes = await reader.detect(element)
          const code = codes[0]?.rawValue
          if (alive && code !== undefined && code !== '') {
            alive = false
            void lookUp(code)
            return
          }
        } catch {
          // A frame that could not be read is a frame; the next one is read all the same.
        }
        if (alive) {
          timer = window.setTimeout(() => void read(), READ_EVERY_MS)
        }
      }
      void read()
    })
    return () => {
      alive = false
      window.clearTimeout(timer)
      setReading(false)
    }
    // The look-up is the same on every render; only whether to read changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning])

  /*
   * Taking and picking both shrink the photo on the device before it goes anywhere, which is
   * work to wait on — the app spec, A press that waits — so each says so on its own control.
   */
  const snapping = useWork()
  const picking = useWork()
  const shrunk = async (from: Blob) => {
    try {
      onPhoto(await downscaleToJpeg(from))
    } catch {
      setSaid('That photo could not be read.')
    }
  }

  const snap = () => {
    const element = video.current
    if (element === null || snapping.working) {
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = element.videoWidth || 1280
    canvas.height = element.videoHeight || 960
    const context = canvas.getContext('2d')
    if (context === null) {
      return
    }
    context.drawImage(element, 0, 0, canvas.width, canvas.height)
    void snapping.run(async () => {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg')
      })
      if (blob === null) {
        setSaid('That photo could not be taken.')
        return
      }
      await shrunk(blob)
    })
  }

  const pick = (file: File | undefined) => {
    if (file === undefined) {
      return
    }
    void picking.run(() => shrunk(file))
  }

  return (
    <div
      aria-label="Camera"
      className="relative flex min-h-[26rem] flex-1 flex-col overflow-hidden rounded-2xl bg-black ring-1 ring-white/10"
    >
      {/* The picture, under everything: the whole of the page, cropped to fill it. */}
      <video
        ref={video}
        autoPlay
        muted
        playsInline
        className={`absolute inset-0 size-full object-cover ${lens === 'open' ? '' : 'hidden'}`}
      />
      {/* The fades the words along the top and the foot are read against. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/70 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/80 via-black/40 to-transparent"
      />

      {lens === 'closed' && missing === null && (
        <p className="absolute inset-0 grid place-items-center px-8 text-center text-sm text-ink-muted">
          The camera could not be opened here.
        </p>
      )}

      {mode === 'barcode' && lens === 'open' && missing === null && (
        /* The window the code is aimed through: four corners, the frame dimmed around them. */
        <div aria-hidden className="absolute inset-0 grid place-items-center">
          <div className="relative h-[34%] w-[70%] shadow-[0_0_0_100vmax_rgb(0_0_0/0.45)]">
            <span className="absolute top-0 left-0 size-6 rounded-tl border-t-[3px] border-l-[3px] border-white" />
            <span className="absolute top-0 right-0 size-6 rounded-tr border-t-[3px] border-r-[3px] border-white" />
            <span className="absolute bottom-0 left-0 size-6 rounded-bl border-b-[3px] border-l-[3px] border-white" />
            <span className="absolute right-0 bottom-0 size-6 rounded-br border-r-[3px] border-b-[3px] border-white" />
          </div>
        </div>
      )}

      {/*
       * Which of the two the camera is for, said on the camera and changed without leaving it:
       * two words on one pill, with the lit one slid to under whichever was pressed.
       */}
      {missing === null && (
        <div
          role="group"
          aria-label="Looking at"
          className="relative z-10 mx-auto mt-4 flex w-52 rounded-full bg-black/45 p-1 ring-1 ring-white/20 backdrop-blur"
        >
          <span
            aria-hidden
            className={`absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-white shadow transition-transform duration-300 ease-out ${
              mode === 'barcode' ? 'translate-x-full' : ''
            }`}
          />
          {(['food', 'barcode'] as const).map((each) => (
            <button
              key={each}
              type="button"
              aria-pressed={mode === each}
              className={`relative z-10 flex-1 rounded-full py-1.5 text-sm font-medium transition-colors duration-300 ${
                mode === each ? 'text-black' : 'text-white/85 hover:text-white'
              }`}
              onClick={() => {
                look(each)
              }}
            >
              {each === 'food' ? 'Food' : 'Barcode'}
            </button>
          ))}
        </div>
      )}

      {/*
       * A code no food carries, standing over the picture until it is answered: made into a
       * food with the code already on it, scanned again, or given up on.
       */}
      {missing !== null && (
        <div className="relative z-10 m-auto flex max-w-xs flex-col items-center gap-3 rounded-2xl bg-surface/95 px-6 py-6 text-center ring-1 ring-white/10 backdrop-blur">
          <h2 className="text-lg font-medium">{`No food carries the code ${missing}`}</h2>
          <p className="text-sm text-ink-muted">Make one yourself and the code is already on it.</p>
          <button
            type="button"
            className={`${PRIMARY} mt-2`}
            onClick={() => {
              onCreate(missing)
            }}
          >
            Create it
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              className={QUIET}
              onClick={() => {
                setMissing(null)
                setSaid(null)
              }}
            >
              Scan again
            </button>
            {/* Giving up is an answer too, and one the camera cannot be left waiting for. */}
            <button type="button" className={QUIET} onClick={onGiveUp}>
              Give up
            </button>
          </div>
        </div>
      )}

      {/* The foot: what the camera says, and the ways off it. */}
      {missing === null && (
        <div className="relative z-10 mt-auto space-y-3 px-4 pb-4">
          {mode === 'barcode' && lens === 'open' && (
            <p role="status" className="text-center text-sm text-white drop-shadow">
              {said ??
                (lookingUp !== null
                  ? 'Looking the code up…'
                  : reading
                    ? 'Point the camera at a barcode'
                    : 'Getting ready to read…')}
            </p>
          )}
          {said !== null && (mode === 'food' || lens === 'closed') && (
            <p role="status" className="text-center text-sm text-white drop-shadow">
              {said}
            </p>
          )}

          {mode === 'food' ? (
            <div className="grid grid-cols-[1fr_auto_1fr] items-center">
              {/* A photo off the device, at the near end. */}
              <label
                title="Pick a photo"
                className={`${OVER} relative cursor-pointer justify-self-start`}
              >
                <Working shown={picking.shown} spinner="size-6">
                  <UploadIcon aria-hidden className="size-6" />
                </Working>
                <input
                  type="file"
                  accept="image/*"
                  aria-label="Pick a photo"
                  aria-busy={picking.working || undefined}
                  disabled={picking.working}
                  className="sr-only"
                  onChange={(event) => {
                    pick(event.target.files?.[0])
                    event.target.value = ''
                  }}
                />
              </label>
              {/* The shutter, in the middle, where a camera's is. */}
              <span className="grid size-16 place-items-center">
                {lens === 'open' && (
                  <button
                    type="button"
                    aria-label="Take a photo"
                    aria-busy={snapping.working || undefined}
                    className="relative grid size-16 place-items-center rounded-full text-white ring-4 ring-white/80 transition-transform active:scale-95"
                    onClick={snap}
                  >
                    <Working shown={snapping.shown} spinner="size-8">
                      <span className="size-12 rounded-full bg-white" />
                    </Working>
                  </button>
                )}
              </span>
              {/* Words alone, at the far end. */}
              <button
                type="button"
                aria-label="Go on with words alone"
                title="Go on with words alone"
                className={`${OVER} justify-self-end`}
                onClick={onWords}
              >
                <NoteIcon aria-hidden className="size-6" />
              </button>
            </div>
          ) : (
            lens === 'closed' && (
              <form
                className="flex items-end gap-3"
                onSubmit={(event) => {
                  event.preventDefault()
                  if (typed.trim() !== '' && lookingUp === null) {
                    void lookUp(typed.trim())
                  }
                }}
              >
                <label className="block min-w-0 flex-1">
                  <span className="mb-1 block text-sm text-ink-muted">Barcode</span>
                  <input
                    aria-label="Barcode"
                    inputMode="numeric"
                    value={typed}
                    onChange={(event) => {
                      setTyped(event.target.value)
                    }}
                    className="w-full rounded-2xl bg-surface p-3 text-lg outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
                  />
                </label>
                <button
                  type="submit"
                  aria-busy={lookingUp !== null || undefined}
                  disabled={lookingUp === null && typed.trim() === ''}
                  className={`relative ${PRIMARY}`}
                >
                  <Working shown={lookingUpShown}>Look up</Working>
                </button>
              </form>
            )
          )}
        </div>
      )}
    </div>
  )
}
